import {Component, OnInit, OnDestroy} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {ConstantsService} from '../ConstantsService';
import {FotoService} from '../../services/foto.service';
import {WizardFlowService} from './wizard-flow.service';
import {
  WizardState, WizardStep, WIZARD_STATE_INICIAL,
  CaminhoEntrada, ModoDeVenda, ProdutoWizard, CategoriaWizard
} from './wizard-state.model';
import {obtenhaTemplatePorSegmento, detecteSegmentoPorTexto} from './segment-templates';
import {SubscriptionLike} from 'rxjs';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'app-wizard-config-empresa-v2',
  templateUrl: './wizard-config-empresa-v2.component.html',
  styleUrls: ['./wizard-config-empresa-v2.component.scss'],
  providers: [WizardFlowService]
})
export class WizardConfigEmpresaV2Component implements OnInit, OnDestroy {
  state: WizardState = {...WIZARD_STATE_INICIAL, passosAtivos: []};
  empresa: any = {};
  animacao: 'slide-esquerda' | 'slide-direita' | '' = '';
  carregando = false;

  private static readonly SESSION_KEY = 'wizard_config_empresa_v2';
  private popStateSub: SubscriptionLike | null = null;
  private navegandoPorHistorico = false;

  constructor(
    private constantsService: ConstantsService,
    private fotoService: FotoService,
    private flowService: WizardFlowService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.restaureEstado();
    this.recalculePassos();

    this.constantsService.empresa$.subscribe(data => {
      if (data && data.id) {
        this.empresa = data;
        if (!this.state.nomeEmpresa && data.nome) this.state.nomeEmpresa = data.nome;
        if (!this.state.logoUrl && data.logo) this.state.logoUrl = data.logo;
        if (!this.state.capaUrl && data.capa) this.state.capaUrl = data.capa;
      }
    });

    // Restaurar passo da URL no carregamento inicial (F5)
    this.route.params.subscribe(params => {
      if (params['passo'] && !this.navegandoPorHistorico) {
        const stepFromUrl = params['passo'] as WizardStep;
        const index = this.state.passosAtivos.indexOf(stepFromUrl);
        if (index >= 0) {
          this.state.passoAtualIndex = index;
        }
      }
    });

    // Escutar botão voltar/avançar do browser
    this.popStateSub = this.location.subscribe(event => {
      if (event.type === 'popstate') {
        const passo = this.extraiaPassoDaUrl(event.url || '');
        if (passo) {
          const index = this.state.passosAtivos.indexOf(passo);
          if (index >= 0) {
            this.navegandoPorHistorico = true;
            const direcao = index < this.state.passoAtualIndex ? 'slide-direita' : 'slide-esquerda';
            this.animacao = direcao;
            setTimeout(() => {
              this.state.passoAtualIndex = index;
              this.animacao = '';
              this.navegandoPorHistorico = false;
            }, 150);
          }
        }
      }
    });

    // Setar URL inicial
    this.atualizarUrl(true);
  }

  ngOnDestroy() {
    if (this.popStateSub) {
      this.popStateSub.unsubscribe();
    }
  }

  private salveEstado() {
    try {
      sessionStorage.setItem(WizardConfigEmpresaV2Component.SESSION_KEY, JSON.stringify(this.state));
    } catch {}
  }

  private restaureEstado() {
    try {
      const json = sessionStorage.getItem(WizardConfigEmpresaV2Component.SESSION_KEY);
      if (json) {
        const saved = JSON.parse(json) as WizardState;
        if (!saved.completou) {
          this.state = saved;
        } else {
          sessionStorage.removeItem(WizardConfigEmpresaV2Component.SESSION_KEY);
        }
      }
    } catch {}
  }

  private extraiaPassoDaUrl(url: string): WizardStep | null {
    const match = url.match(/wizard\/([a-z-]+)/);
    if (match) {
      const valor = match[1] as WizardStep;
      if (Object.values(WizardStep).includes(valor)) {
        return valor;
      }
    }
    return null;
  }

  private atualizarUrl(replace = false) {
    const passo = this.passoAtual;
    const url = `/admin/wizard/${passo}`;
    if (replace) {
      this.location.replaceState(url);
    } else {
      this.location.go(url);
    }
  }

  get passoAtual(): WizardStep {
    return this.flowService.obtenhaPassoAtual(this.state);
  }

  get progresso(): number {
    return this.flowService.obtenhaProgresso(this.state);
  }

  get podeAvancar(): boolean {
    return this.flowService.podeAvancar(this.state);
  }

  get podeVoltar(): boolean {
    return this.flowService.podeVoltar(this.state);
  }

  recalculePassos() {
    this.state.passosAtivos = this.flowService.calculePassosAtivos(this.state);
  }

  avance() {
    if (!this.podeAvancar) return;
    this.animacao = 'slide-esquerda';
    setTimeout(() => {
      this.state.passoAtualIndex = this.flowService.avance(this.state);
      this.animacao = '';
      if (!this.navegandoPorHistorico) {
        this.atualizarUrl();
      }
      this.salveEstado();
    }, 150);
  }

  volte() {
    if (!this.podeVoltar) return;
    this.animacao = 'slide-direita';
    setTimeout(() => {
      this.state.passoAtualIndex = this.flowService.volte(this.state);
      this.animacao = '';
      if (!this.navegandoPorHistorico) {
        this.atualizarUrl();
      }
      this.salveEstado();
    }, 150);
  }

  private vaParaPassoAvante(step: WizardStep) {
    const novoIndex = this.flowService.vaParaPassoQualquer(this.state, step);
    if (novoIndex !== this.state.passoAtualIndex) {
      this.animacao = 'slide-esquerda';
      setTimeout(() => {
        this.state.passoAtualIndex = novoIndex;
        this.animacao = '';
        if (!this.navegandoPorHistorico) {
          this.atualizarUrl();
        }
        this.salveEstado();
      }, 150);
    }
  }

  vaParaPasso(step: WizardStep) {
    const novoIndex = this.flowService.vaParaPasso(this.state, step);
    if (novoIndex !== this.state.passoAtualIndex) {
      this.animacao = novoIndex < this.state.passoAtualIndex ? 'slide-direita' : 'slide-esquerda';
      setTimeout(() => {
        this.state.passoAtualIndex = novoIndex;
        this.animacao = '';
        if (!this.navegandoPorHistorico) {
          this.atualizarUrl();
        }
        this.salveEstado();
      }, 150);
    }
  }

  // --- Callbacks dos steps ---

  async onBoasVindas(dados: {nome: string; segmento: string | null; logoUrl?: string | null}) {
    this.state.nomeEmpresa = dados.nome;
    this.state.segmento = dados.segmento;
    if (dados.logoUrl) this.state.logoUrl = dados.logoUrl;
    this.state.subdominio = this.gerarSlug(dados.nome);

    if (this.state.caminhoEscolhido === 'template' && this.state.segmento) {
      const template = obtenhaTemplatePorSegmento(this.state.segmento);
      this.state.produtosTemplate = [];
      template.categorias.forEach(cat => {
        cat.produtos.forEach(p => {
          this.state.produtosTemplate.push({...p, selecionado: true, exemplo: true});
        });
      });
      this.state.categoriasImportadas = template.categorias.map(c => ({...c}));
    }

    await this.salveEmpresa();
    this.recalculePassos();
    this.avance();
  }

  onCaminhoEscolhido(caminho: CaminhoEntrada) {
    this.state.caminhoEscolhido = caminho;
    this.recalculePassos();
    this.vaParaPassoAvante(WizardStep.BOAS_VINDAS);
  }

  async onLogoSalvo(logoUrl: string) {
    this.state.logoUrl = logoUrl;
    await this.salveEmpresa();
    this.avance();
  }

  onSubdominioConcluido(subdominio: string) {
    this.state.subdominio = subdominio;
    this.avance();
  }

  onModoVendaSelecionado(modos: ModoDeVenda[]) {
    this.state.modosDeVenda = modos;
    this.recalculePassos();
    this.avance();
  }

  onProdutosConcluidos() {
    this.avance();
  }

  onTemaSelecionado(temaCodigo: string) {
    this.state.temaSelecionado = temaCodigo;
    this.avance();
  }

  async onImportacaoConcluida(dados: {produtos: ProdutoWizard[]; categorias: CategoriaWizard[]; logo?: string; capa?: string; nomeEmpresa?: string | null; telefone?: string; enderecoImportado?: string; cep?: string; latitudeLongitude?: string; horarios?: any[]}) {
    this.state.caminhoEscolhido = 'importar';
    this.state.produtosImportados = dados.produtos;
    this.state.categoriasImportadas = dados.categorias;
    if (dados.nomeEmpresa) {
      this.state.nomeEmpresa = dados.nomeEmpresa;
      this.state.subdominio = this.gerarSlug(dados.nomeEmpresa);
    }
    if (dados.logo) {
      this.state.logoUrl = dados.logo;
    }
    if (dados.capa) this.state.capaUrl = dados.capa;
    if (dados.telefone) this.state.telefone = dados.telefone;
    if (dados.enderecoImportado) this.state.enderecoImportado = dados.enderecoImportado;
    if (dados.cep) this.state.cep = dados.cep;
    if (dados.latitudeLongitude) this.state.latitudeLongitude = dados.latitudeLongitude;
    if (!this.state.segmento) {
      const texto = [dados.nomeEmpresa || '', ...dados.categorias.map(c => c.nome)].join(' ');
      const detectado = detecteSegmentoPorTexto(texto);
      if (detectado) this.state.segmento = detectado;
    }
    await this.salveEmpresa();
    this.recalculePassos();
    this.vaParaPassoAvante(WizardStep.BOAS_VINDAS);
  }

  async onConcluir() {
    const novaAba = this.state.subdominio ? window.open('', '_blank') : null;
    await this.salveEmpresa(true);
    this.state.completou = true;
    sessionStorage.removeItem(WizardConfigEmpresaV2Component.SESSION_KEY);

    if (novaAba && this.state.subdominio) {
      novaAba.location.href = `https://${this.state.subdominio}.meucardapio.ai`;
    }

    this.router.navigate(['/admin/index']);
  }

  private async salveEmpresa(salveDominio = false): Promise<void> {
    if (!this.empresa?.id) return;

    const nomeDesejado = this.state.nomeEmpresa ? this.state.nomeEmpresa.trim() : '';
    const dominioDesejado = this.state.subdominio ? this.state.subdominio.trim() : '';
    const logoDesejado = this.state.logoUrl ? this.state.logoUrl.trim() : '';
    const enderecoDesejado = this.state.enderecoImportado ? this.state.enderecoImportado.trim() : '';
    const cepDesejado = this.state.cep ? this.state.cep.trim() : '';
    const telefoneDesejado = this.state.telefone ? this.state.telefone.trim() : '';
    const capaDesejada = this.state.capaUrl ? this.state.capaUrl.trim() : '';
    const latLonDesejado = this.state.latitudeLongitude ? this.state.latitudeLongitude.trim() : '';

    const nomeAnterior = this.empresa.nome;
    const dominioAnterior = this.empresa.dominio;
    const logoAnterior = this.empresa.logo;
    const capaAnterior = this.empresa.capa;

    this.sincronizeEmpresaComEstado();

    const dados: any = {id: this.empresa.id};
    let mudouEmpresa = false;

    if (nomeDesejado && nomeDesejado !== nomeAnterior) {
      dados.nome = nomeDesejado;
      mudouEmpresa = true;
    }

    if (salveDominio && dominioDesejado && dominioDesejado !== dominioAnterior && environment.production) {
      dados.dominio = dominioDesejado;
      mudouEmpresa = true;
    }

    if (enderecoDesejado && enderecoDesejado !== this.empresa.endereco) {
      dados.endereco = enderecoDesejado;
      mudouEmpresa = true;
    }

    if (cepDesejado && cepDesejado !== this.empresa.cep) {
      dados.cep = cepDesejado;
      mudouEmpresa = true;
    }

    if (telefoneDesejado && telefoneDesejado !== this.empresa.whatsapp) {
      dados.whatsapp = telefoneDesejado;
      mudouEmpresa = true;
    }

    if (latLonDesejado && latLonDesejado !== this.empresa.latitudeLongitude) {
      dados.latitudeLongitude = latLonDesejado;
      mudouEmpresa = true;
    }

    try {
      if (mudouEmpresa) {
        await this.http.put('/empresas', dados).toPromise();
      }

      if (logoDesejado && logoDesejado !== logoAnterior) {
        await this.fotoService.salveLogoEmpresa(this.empresa);
      }

      if (capaDesejada && capaDesejada !== capaAnterior) {
        await this.fotoService.salveCapaEmpresa(this.empresa);
      }

      if (mudouEmpresa || logoAnterior !== this.empresa.logo || capaAnterior !== this.empresa.capa ||
          nomeAnterior !== this.empresa.nome || dominioAnterior !== this.empresa.dominio) {
        this.constantsService.mudouEmpresa({...this.empresa});
      }
    } catch (e) {
      console.error('[Wizard] Erro ao salvar empresa:', e);
    }
  }

  private sincronizeEmpresaComEstado() {
    if (!this.empresa) this.empresa = {};

    if (this.state.nomeEmpresa) {
      this.empresa.nome = this.state.nomeEmpresa;
    }

    if (this.state.subdominio) {
      this.empresa.dominio = this.state.subdominio;
    }

    if (this.state.logoUrl) {
      this.empresa.logo = this.state.logoUrl;
    }

    if (this.state.capaUrl) {
      this.empresa.capa = this.state.capaUrl;
    }

    if (this.state.enderecoImportado) {
      this.empresa.endereco = this.state.enderecoImportado;
    }

    if (this.state.cep) {
      this.empresa.cep = this.state.cep;
    }

    if (this.state.telefone) {
      this.empresa.whatsapp = this.state.telefone;
    }

    if (this.state.latitudeLongitude) {
      this.empresa.latitudeLongitude = this.state.latitudeLongitude;
    }
  }

  private gerarSlug(nome: string): string {
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '')
      .substring(0, 30);
  }
}
